import {
    Notification,
    dismissNotification,
    getNotifications,
} from "@client/features/notifications";
import {useAppDispatch, useAppSelector} from "@client/store";
import {
    CheckCircleIcon,
    InformationCircleIcon,
    XCircleIcon,
} from "@heroicons/react/24/outline";
import {XMarkIcon} from "@heroicons/react/24/solid";
import {classNames} from "@reactivated";
import {AnimatePresence, motion} from "motion/react";
import React from "react";
import ReactMarkdown from "react-markdown";

const ICONS = {
    success: [CheckCircleIcon, "text-green-500"],
    info: [InformationCircleIcon, "text-indigo-600"],
} as const;

// Bold and italic only. Paragraphs render as text with their blank line, so
// the preserved breaks keep the message's own line breaks.
function ToastMessage({message}: {message: string}) {
    return (
        <ReactMarkdown
            allowedElements={["p", "strong", "em"]}
            unwrapDisallowed
            components={{
                p: ({children}) => (
                    <>
                        {children}
                        {"\n"}
                    </>
                ),
            }}
        >
            {message}
        </ReactMarkdown>
    );
}

function Toast({
    notification,
    onDismiss,
}: {
    notification: Notification;
    onDismiss?: () => void;
}) {
    const [Icon, iconColor] =
        notification.level_tag === "success" || notification.level_tag === "info"
            ? ICONS[notification.level_tag]
            : [XCircleIcon, "text-red-500"];

    // Wrapped text keeps its box at the full max width, leaving a gap on the
    // right. Size the text to its widest line instead, rounded up so it does
    // not rewrap.
    const text = React.useRef<HTMLSpanElement>(null);
    React.useLayoutEffect(() => {
        const span = text.current;
        if (span == null) {
            return;
        }
        span.style.width = "";
        const range = document.createRange();
        range.selectNodeContents(span);
        span.style.width = `${Math.ceil(range.getBoundingClientRect().width)}px`;
    }, [notification.message]);

    return (
        <div
            // The class is SceneModal's outside-click guard: taps here do not close it.
            className={classNames(
                "joy-notification",
                "pointer-events-auto inline-flex items-start gap-2 rounded-xl border border-gray-200/90 bg-white/95 px-4 py-2 text-left backdrop-blur-md",
            )}
        >
            <Icon className={classNames("size-5 shrink-0", iconColor)} />
            <span
                ref={text}
                // Not whitespace-pre-line: the white-space shorthand resets
                // text-wrap, which turns text-balance off.
                className="text-sm font-medium text-balance text-gray-900 [white-space-collapse:preserve-breaks]"
            >
                <ToastMessage message={notification.message} />
            </span>
            {onDismiss != null && (
                <button
                    type="button"
                    onClick={onDismiss}
                    aria-label="Dismiss notification"
                    className="ml-1 flex h-5 items-center text-gray-400 hover:text-gray-600"
                >
                    <XMarkIcon className="size-4" />
                </button>
            )}
        </div>
    );
}

// Out of flow, a step above the positioned bar it is mounted in: content
// never moves when a toast comes or goes.
export function Toasts() {
    const notifications = useAppSelector(getNotifications);
    const dispatch = useAppDispatch();

    return (
        <div className="pointer-events-none absolute inset-x-0 bottom-full mb-joy-step flex justify-center px-4">
            <div className="flex max-w-sm flex-col gap-joy-step">
                {/* Not popLayout: it pins a leaving toast at its offsetWidth,
                    which rounds down, so the text rewraps as it fades. */}
                <AnimatePresence initial={false}>
                    {notifications.map((notification) => (
                        <motion.div
                            key={notification.id}
                            // Position only: a full layout animation scales the
                            // element to animate its size, and scaled text shimmers
                            // on the way out. AnimatedButton avoids layout for the
                            // same reason.
                            layout="position"
                            initial={{opacity: 0, y: 8}}
                            animate={{opacity: 1, y: 0}}
                            exit={{
                                opacity: 0,
                                y: 4,
                                transition: {duration: 0.15, ease: "easeIn"},
                            }}
                            // One tween for both: y defaults to a spring, which
                            // overshoots and ends apart from the fade.
                            transition={{duration: 0.2, ease: "easeOut"}}
                            className="flex transform-gpu justify-center"
                        >
                            <Toast
                                notification={notification}
                                onDismiss={
                                    notification.permanent === true
                                        ? () =>
                                              dispatch(
                                                  dismissNotification(notification),
                                              )
                                        : undefined
                                }
                            />
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </div>
    );
}
